import { useState, useEffect, useRef } from 'react'

const SWAP_WORDS = ['settles.', 'clears.', 'moves.', 'lands.']

export default function Hero() {
  const [showHubSpotEmbed, setShowHubSpotEmbed] = useState(false)
  const [wordIdx, setWordIdx] = useState(0)
  const canvasRef = useRef(null)
  const HUBSPOT_MEETING_URL = process.env.NEXT_PUBLIC_HUBSPOTAPI_KEY

  const utmParams = {
    ref: 'secure-flow-hero',
    utm_source: 'website',
    utm_medium: 'cta',
    utm_campaign: 'secure_flow',
    utm_content: 'secure_flow__hero__talk_to_experts',
  }

  const addUtmToUrl = (url) => {
    if (!url) return url
    const params = new URLSearchParams(utmParams)
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}${params.toString()}`
  }

  const openHubSpotBooking = (e) => {
    if (e?.preventDefault) e.preventDefault()
    setShowHubSpotEmbed(true)
    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href)
      Object.entries(utmParams).forEach(([key, value]) => {
        currentUrl.searchParams.set(key, value)
      })
      window.history.replaceState({}, '', currentUrl.pathname + '?' + currentUrl.searchParams.toString())
    }
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden'
    }
  }

  const closeHubSpotBooking = () => {
    setShowHubSpotEmbed(false)
    if (typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href)
      Object.keys(utmParams).forEach((key) => {
        currentUrl.searchParams.delete(key)
      })
      const newQuery = currentUrl.searchParams.toString()
      window.history.replaceState({}, '', currentUrl.pathname + (newQuery ? `?${newQuery}` : ''))
    }
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'unset'
    }
  }

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showHubSpotEmbed) closeHubSpotBooking()
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', handleEscape)
      return () => window.removeEventListener('keydown', handleEscape)
    }
  }, [showHubSpotEmbed])

  useEffect(() => {
    const reduced = typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced) return
    const id = setInterval(() => {
      setWordIdx((i) => (i + 1) % SWAP_WORDS.length)
    }, 2400)
    return () => clearInterval(id)
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d', { alpha: true })
    if (!ctx) return
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const C = {
      bg: [5, 11, 25],
      blue: [14, 165, 233],
      cyan: [6, 182, 212],
      lightBlue: [56, 189, 248],
      green: [34, 197, 94],
      greenLight: [74, 222, 128],
    }
    const rgba = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${a})`
    let W = 0, H = 0
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    let raf = 0
    let lastTs = performance.now()
    let visible = true
    let particles = []
    let waves = []
    const mouse = { x: -9999, y: -9999 }

    const build = () => {
      particles = []
      const N = Math.min(80, Math.floor((W * H) / 15000))
      for (let i = 0; i < N; i++) {
        particles.push({
          x: Math.random() * W,
          y: Math.random() * H,
          vx: (Math.random() - 0.3) * 0.4,
          vy: (Math.random() - 0.5) * 0.2,
          r: 0.6 + Math.random() * 1.4,
          hue: Math.random() < 0.7 ? C.lightBlue : (Math.random() < 0.5 ? C.cyan : C.greenLight),
          phase: Math.random() * Math.PI * 2,
        })
      }
      waves = []
      for (let i = 0; i < 4; i++) {
        waves.push({
          y: H * (0.2 + 0.18 * i),
          amp: 30 + Math.random() * 20,
          freq: 0.005 + Math.random() * 0.002,
          speed: 0.0003 + Math.random() * 0.0002,
          phase: Math.random() * Math.PI * 2,
          alpha: 0.04 + 0.02 * i,
          hue: i < 2 ? C.blue : (i === 2 ? C.cyan : C.green),
        })
      }
    }

    const resize = () => {
      const r = canvas.getBoundingClientRect()
      W = r.width
      H = r.height
      canvas.width = Math.floor(W * dpr)
      canvas.height = Math.floor(H * dpr)
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      build()
    }

    const step = (ts) => {
      raf = requestAnimationFrame(step)
      const dt = Math.min(48, ts - lastTs)
      lastTs = ts
      if (!visible) return
      const dts = dt / 1000

      ctx.fillStyle = rgba(C.bg, 0.20)
      ctx.fillRect(0, 0, W, H)

      ctx.fillStyle = rgba(C.blue, 0.05)
      const ds = 44
      for (let x = (ds / 2) | 0; x < W; x += ds) {
        for (let y = (ds / 2) | 0; y < H; y += ds) ctx.fillRect(x, y, 1, 1)
      }

      for (const w of waves) {
        w.phase += w.speed * dt
        ctx.strokeStyle = rgba(w.hue, w.alpha)
        ctx.lineWidth = 1
        ctx.beginPath()
        for (let x = 0; x <= W; x += 10) {
          const y = w.y + Math.sin(x * w.freq + w.phase) * w.amp
          if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y)
        }
        ctx.stroke()
      }

      for (const p of particles) {
        p.x += p.vx * dts * 60
        p.y += p.vy * dts * 60
        p.phase += 0.02
        if (p.x > W + 20) p.x = -20
        if (p.x < -20) p.x = W + 20
        if (p.y > H + 20) p.y = -20
        if (p.y < -20) p.y = H + 20
        const md = Math.hypot(mouse.x - p.x, mouse.y - p.y)
        if (md < 120) {
          const f = (1 - md / 120) * 0.4
          p.vx += (mouse.x - p.x) * 0.0002 * f
          p.vy += (mouse.y - p.y) * 0.0002 * f
        }
        const pulse = 0.7 + 0.3 * Math.sin(p.phase)
        ctx.fillStyle = rgba(p.hue, 0.4 * pulse)
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r * 3, 0, Math.PI * 2); ctx.fill()
        ctx.fillStyle = rgba(p.hue, 0.85 * pulse)
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill()
      }

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i], b = particles[j]
          const d = Math.hypot(a.x - b.x, a.y - b.y)
          if (d < 100) {
            ctx.strokeStyle = rgba(C.blue, 0.08 * (1 - d / 100))
            ctx.lineWidth = 0.5
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke()
          }
        }
      }

      if (mouse.x >= 0) {
        const g = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, 160)
        g.addColorStop(0, rgba(C.cyan, 0.10))
        g.addColorStop(0.6, rgba(C.cyan, 0.03))
        g.addColorStop(1, rgba(C.cyan, 0))
        ctx.fillStyle = g
        ctx.beginPath(); ctx.arc(mouse.x, mouse.y, 160, 0, Math.PI * 2); ctx.fill()
      }

      if (reduced) cancelAnimationFrame(raf)
    }

    const ro = new ResizeObserver(resize)
    ro.observe(canvas)
    const io = new IntersectionObserver((es) => { visible = es[0]?.isIntersecting ?? true })
    io.observe(canvas)
    const onMove = (e) => {
      const r = canvas.getBoundingClientRect()
      mouse.x = e.clientX - r.left
      mouse.y = e.clientY - r.top
    }
    window.addEventListener('mousemove', onMove, { passive: true })
    resize()
    raf = requestAnimationFrame(step)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      io.disconnect()
      window.removeEventListener('mousemove', onMove)
    }
  }, [])

  return (
    <section className="sf-hero" id="hero">
      <div className="sf-hero-bg"><canvas ref={canvasRef} className="sf-hero-canvas" /></div>
      <div className="sf-hero-wash" />
      <div className="sf-hero-vignette" />
      <div className="sf-hero-noise" />
      <div className="sf-hero-bg-fade" />

      <div className="sf-container sf-hero-inner" id="top">
        <div className="sf-brand-mark" aria-label="SecureFlow" data-sf-hero-brand>
          <svg className="sf-brand-icon" viewBox="0 0 880 625" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M503.519 123.329L739.387 359.197C751.951 371.761 751.962 392.12 739.412 404.671L529.211 614.871C516.661 627.422 496.302 627.41 483.738 614.846L432.552 563.661L475.479 520.735C513.516 482.698 532.753 445.109 533.192 407.969C534.089 371.288 518.251 336.661 485.678 304.088L467.531 285.941L467.626 285.843L446.982 265.198C432.76 250.976 426.562 236.071 428.387 220.482C429.296 204.893 436.624 190.224 450.372 176.476L503.519 123.329ZM443.649 63.4593L503.519 123.329L521.863 104.985C536.07 90.7783 553.49 83.9099 574.124 84.3796C594.758 84.8494 613.562 93.5715 630.537 110.546L822.084 302.093C828.366 308.375 838.545 308.381 844.82 302.106L874.339 272.587C880.615 266.311 880.609 256.132 874.327 249.85L689.662 65.1843C649.748 25.2711 609.616 4.38647 569.265 2.53047C527.997 0.674006 487.886 19.2224 448.933 58.1758L443.649 63.4593ZM372.683 503.791L432.552 563.661L430.11 566.104C391.156 605.057 351.045 623.606 309.777 621.749C269.426 619.893 229.294 599.009 189.381 559.096L4.71511 374.43C-1.56687 368.148 -1.57241 357.968 4.70275 351.693L34.2217 322.174C40.4969 315.899 50.6765 315.905 56.9585 322.187L248.506 513.734C265.48 530.708 284.284 539.43 304.918 539.9C325.552 540.37 342.972 533.502 357.179 519.295L372.683 503.791ZM372.683 503.791L428.67 447.804C442.418 434.056 449.746 419.387 450.655 403.798C452.481 388.209 446.282 373.303 432.061 359.082L411.416 338.437L411.511 338.339L393.364 320.192C360.791 287.619 344.954 252.992 345.851 216.311C346.289 179.17 365.527 141.582 403.563 103.545L443.649 63.4593L389.62 9.43022C377.056 -3.13374 356.697 -3.14482 344.147 9.40552L133.946 219.606C121.396 232.156 121.407 252.516 133.971 265.08L372.683 503.791Z"
            />
          </svg>
          <span className="sf-brand-wordmark">SecureFlow</span>
        </div>
        <span className="sf-hero-eyebrow"><span className="sf-pulse-d" />by Cloudastra Technologies</span>
        <h1 className="sf-hero-h1">
          <span className="sf-line">Stop the <span className="sf-under sf-grad">loss</span></span>
          <br />
          <span className="sf-line">
            before money{' '}
            <span className="sf-word-stack" aria-live="polite">
              <span key={wordIdx} className="sf-grad sf-word-anim">{SWAP_WORDS[wordIdx]}</span>
            </span>
          </span>
        </h1>
        <p className="sf-hero-sub">
          SecureFlow scores every UPI, IMPS, RTGS and cross-border payment against your rules,
          and against live sanctions and mule signals, the moment it&rsquo;s initiated.
          Returns <strong>pass / flag / block</strong> before funds clear.
        </p>
        <div className="sf-hero-cta">
          <button type="button" onClick={openHubSpotBooking} className="sf-btn sf-btn-blue sf-btn-lg">
            <span>Book a demo</span>
            <svg className="sf-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 7h8m-3-3l3 3-3 3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <a
            href="https://secureflow.cloudastra.co"
            target="_blank"
            rel="noopener noreferrer"
            className="sf-btn sf-btn-ghost-light sf-btn-lg"
          >
            Explore SecureFlow
            <svg className="sf-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M3 7h8m-3-3l3 3-3 3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
        <p className="sf-hero-cta-fine">Pilot in weeks, not quarters · India-resident hosting · Native ISO 20022</p>
        <div className="sf-hero-trust">
          {['Native ISO 20022', 'India-resident hosting', 'Live interdiction', 'No-code rule editor'].map((t) => (
            <span key={t}>
              <svg viewBox="0 0 14 14" fill="none">
                <path d="M2 7l3 3 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              {t}
            </span>
          ))}
        </div>
      </div>

      {showHubSpotEmbed && (
        <div
          id='hubspot-embed-section'
          className='fixed inset-0 z-[9999] flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center'
          onClick={closeHubSpotBooking}
        >
          <div
            className='relative w-full overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:mx-4 sm:max-w-4xl sm:rounded-2xl'
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeHubSpotBooking}
              aria-label='Close booking calendar'
              className='absolute top-2 right-3 z-20 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-md text-gray-700 transition-colors hover:bg-gray-100'
            >
              <svg className='h-4 w-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2.5} d='M6 18L18 6M6 6l12 12' />
              </svg>
            </button>
            <div className='overflow-hidden' style={{ marginTop: '-48px' }}>
              <iframe
                src={addUtmToUrl(HUBSPOT_MEETING_URL)}
                title='Schedule a Call'
                className='w-full border-0'
                style={{ height: 'calc(85vh + 48px)', border: 'none' }}
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .sf-hero {
          position: relative; isolation: isolate; overflow: hidden;
          padding: 140px 0 110px;
          min-height: 90vh;
          background: var(--bg-dark);
          color: var(--text-light);
        }
        .sf-hero-bg {
          position: absolute; inset: 0; z-index: -3;
          pointer-events: none; overflow: hidden;
        }
        .sf-hero-canvas {
          position: absolute; inset: -24px;
          width: calc(100% + 48px); height: calc(100% + 48px);
          display: block;
        }
        .sf-hero-wash {
          position: absolute; inset: 0; z-index: -2;
          background:
            linear-gradient(180deg,rgba(5,11,25,0.40) 0%,rgba(5,11,25,0.25) 45%,rgba(5,11,25,0.88) 100%),
            radial-gradient(70% 60% at 30% 0%,rgba(14,165,233,0.20),transparent 70%),
            radial-gradient(50% 40% at 80% 85%,rgba(34,197,94,0.12),transparent 70%);
        }
        .sf-hero-vignette {
          position: absolute; inset: 0; z-index: -2;
          background: radial-gradient(40% 35% at 50% 48%,rgba(5,11,25,0.55),transparent 70%);
          pointer-events: none;
        }
        .sf-hero-bg-fade {
          position: absolute; inset: auto 0 0 0; height: 200px; z-index: -1;
          background: linear-gradient(to bottom,transparent,var(--bg-dark));
          pointer-events: none;
        }
        .sf-hero-noise {
          position: absolute; inset: 0; z-index: -1;
          pointer-events: none; opacity: 0.4; mix-blend-mode: overlay;
          background-image: url("data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0 0.04 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
        }
        .sf-hero-inner {
          max-width: 1080px; margin: 0 auto; text-align: center;
          display: flex; flex-direction: column; align-items: center;
          gap: clamp(22px,2.5vw,30px);
          position: relative; z-index: 2;
        }
        .sf-brand-mark {
          display: inline-flex; align-items: center;
          gap: clamp(10px, 1vw, 14px);
          margin-bottom: clamp(4px, 0.5vw, 8px);
        }
        .sf-brand-icon {
          width: clamp(56px, 6vw, 84px); height: auto;
          fill: var(--sf-500);
          filter: drop-shadow(0 6px 20px rgba(34,197,94,0.28));
        }
        .sf-brand-wordmark {
          font-size: clamp(34px, 3.6vw, 52px);
          font-weight: 700; letter-spacing: -0.02em;
          line-height: 1; color: var(--text-light);
        }
        .sf-hero-eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          font-family: var(--mono); font-size: 14px; font-weight: 600;
          letter-spacing: 0.18em; text-transform: uppercase;
          color: var(--ca-200); background: rgba(14,165,233,0.14);
          border: 1px solid rgba(14,165,233,0.32);
          padding: 10px 20px; border-radius: 999px;
          backdrop-filter: blur(10px);
        }
        .sf-pulse-d {
          width: 8px; height: 8px; border-radius: 50%;
          background: var(--ca-400);
          box-shadow: 0 0 0 5px rgba(14,165,233,0.22);
          animation: sfHeroPulse 2.4s ease-in-out infinite;
        }
        @keyframes sfHeroPulse { 0%,100%{opacity:0.6} 50%{opacity:1} }
        .sf-hero-h1 {
          font-size: clamp(2.5rem,6.5vw,5.5rem);
          font-weight: 600; letter-spacing: -0.03em;
          line-height: 0.96; color: var(--text-light);
          text-wrap: balance;
        }
        .sf-grad {
          background: linear-gradient(180deg,#FFFFFF 0%,#BAE6FD 50%,#22C55E 100%);
          -webkit-background-clip: text; background-clip: text; color: transparent;
        }
        .sf-under { position: relative; display: inline-block; }
        .sf-under::after {
          content: ''; position: absolute; inset: auto 0 -6px 0; height: 6px;
          background: linear-gradient(to right,rgba(14,165,233,0),rgba(14,165,233,1) 50%,rgba(34,197,94,1) 50%,rgba(34,197,94,0));
          filter: blur(4px);
        }
        .sf-word-stack {
          display: inline-flex; flex-direction: column;
          overflow: hidden; vertical-align: top;
          height: 1em; line-height: 1em;
        }
        .sf-word-stack span { line-height: 1em; }
        .sf-word-anim {
          display: inline-block;
          animation: sfWordIn 0.6s cubic-bezier(0.65,0,0.35,1);
        }
        @keyframes sfWordIn {
          from { transform: translateY(100%); opacity: 0; filter: blur(8px); }
          to { transform: translateY(0); opacity: 1; filter: blur(0); }
        }
        .sf-hero-sub {
          font-size: clamp(0.95rem,1.18vw,1.13rem);
          color: var(--text-light-2); line-height: 1.7;
          max-width: 62ch; text-wrap: pretty;
        }
        .sf-hero-sub strong { color: rgba(232,241,255,0.95); font-weight: 500; }
        .sf-hero-cta {
          display: flex; gap: 12px; flex-wrap: wrap; justify-content: center;
        }
        .sf-hero-cta-fine {
          margin-top: 14px; font-size: 13px;
          color: var(--text-light-3); letter-spacing: 0.02em;
        }
        .sf-hero-trust {
          display: flex; flex-wrap: wrap; justify-content: center;
          gap: 8px 22px; font-size: 12px; color: var(--text-light-3);
        }
        .sf-hero-trust span {
          display: inline-flex; align-items: center; gap: 6px;
        }
        .sf-hero-trust svg { width: 12px; height: 12px; color: var(--ca-400); }
      `}</style>
    </section>
  )
}
